"use client"

import { useEffect, useId, useRef, useState } from "react"
import { isValidPhone } from "@/utils/phone"
import { PhoneInput } from "@ui/phone-input"
import { Label } from "@ui/shad"
import { Phone, X } from "lucide-react"
import { toast } from "sonner"

import { useCreatePhoneCallCapture } from "@/hooks/api/phone-call-captures/mutations"
import { useFetchCurrentUser } from "@/hooks/api/users/queries"
import { SimpleFormDialog } from "@/components/dialogs"
import { PartnerFeatureDialogGate } from "@/components/partners/partner-feature-gate"

interface CallMyPhoneDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  householdId?: string
  meetingId?: string
  title?: string
}

function CallMyPhoneDialogContent({ open, onOpenChange, householdId, meetingId, title }: CallMyPhoneDialogProps) {
  const dialogFormId = useId()

  const [phoneNumber, setPhoneNumber] = useState("")
  const { data: currentUser } = useFetchCurrentUser()
  const createPhoneCallCaptureMutation = useCreatePhoneCallCapture()
  const hasInitialized = useRef(false)

  useEffect(() => {
    if (open && currentUser?.unformatted_phone && !hasInitialized.current) {
      setPhoneNumber(currentUser.unformatted_phone)
      hasInitialized.current = true
    }
    if (!open) {
      hasInitialized.current = false
    }
  }, [open, currentUser?.unformatted_phone])

  const isSubmitting = createPhoneCallCaptureMutation.isPending

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()

    await createPhoneCallCaptureMutation.mutateAsync({ phoneNumber, householdId, meetingId, title })

    toast.success("Calling your phone now...")
    onOpenChange(false)
    setPhoneNumber("")
  }

  return (
    <SimpleFormDialog
      title={"Call my phone"}
      open={open}
      onOpenChange={(nextOpen) => !isSubmitting && onOpenChange(nextOpen)}
      action={{
        label: (
          <>
            <Phone />
            {isSubmitting ? "Calling..." : "Call me"}
          </>
        ),
        form: `${dialogFormId}-dialog-call-my-phone-dialog-50`,
        disabled: isSubmitting || !isValidPhone(phoneNumber) || !phoneNumber,
      }}
      cancel={{ label: "Cancel", onClick: () => onOpenChange(false), disabled: isSubmitting }}
    >
      <form
        id={`${dialogFormId}-dialog-call-my-phone-dialog-50`}
        onSubmit={handleSubmit}
        className="flex min-h-0 flex-1 flex-col gap-2"
      >
        <>
          <p className="leading-tight">
            You&apos;ll get a call from <span className="font-medium">(888) 565-3692</span>.
          </p>
          <p className="leading-tight">
            Either record an in-person meeting on speaker or merge with another call to record the phone call.
          </p>

          <div className="space-y-2">
            <Label htmlFor="phone-number">Your phone number</Label>
            <div className="relative">
              <PhoneInput value={phoneNumber} onChange={setPhoneNumber} disabled={isSubmitting} />
              {phoneNumber && !isSubmitting && (
                <button
                  type="button"
                  onClick={() => setPhoneNumber("")}
                  className="text-muted-foreground hover:text-foreground absolute top-1/2 right-2 -translate-y-1/2"
                >
                  <X className="size-4" />
                </button>
              )}
            </div>
          </div>
        </>
      </form>
    </SimpleFormDialog>
  )
}

export function CallMyPhoneDialog(props: React.ComponentProps<typeof CallMyPhoneDialogContent>) {
  return (
    <PartnerFeatureDialogGate
      feature="notetaker"
      title="Call my phone"
      open={props.open}
      onOpenChange={props.onOpenChange}
    >
      <CallMyPhoneDialogContent {...props} />
    </PartnerFeatureDialogGate>
  )
}
